// Binary CTG recordings read by fhrviewer.js (SOUP, docs/soup/fhrviewer.md), re-implemented here for the server
// (upload metadata, cropping) and the back-office editors:
//   header: [uint32 epoch] (4 bytes, V2 / dataset files) or [uint32 magic 1234555][uint32 epoch] (8, recorder
//           files), optionally followed by a legacy stream code word (12).
//   samples at 4 Hz: .rcf / FHRMA .fhr 6 bytes, .rcfm and OpenCTG .fhr 8 bytes, .rcfa 12 bytes.
// The header is identified by the magic number, like the play layer (src/lib/play/trace.ts), not by divisibility:
// V2 files may end with half a sample, which divisibility reads as an 8-byte header, shifting every sample (the FHR
// then lands in the MHR channel and the contractions vanish: owner's screenshot of 08/10, trace 1721310551-1.rcfm).
export const SAMPLE_RATE_HZ = 4

export const traceFormats = ['rcf', 'rcfm', 'rcfa', 'fhr8'] as const
export type TraceFormat = (typeof traceFormats)[number]

const BYTES_PER_SAMPLE: Record<TraceFormat, number> = { rcf: 6, rcfm: 8, rcfa: 12, fhr8: 8 }
const MAGIC = 1234555
const CODE_RECORDING = 1234567
const CODE_FINISHED = 1234568

export const bytesPerSample = (format: TraceFormat): number => BYTES_PER_SAMPLE[format]

// Extension handed to the viewer (it only uses it for the download name once the layout is forced).
export const viewerExtension = (format: TraceFormat): string => (format === 'fhr8' ? 'fhr' : format)

const extensionOf = (filename: string): string => (filename.toLowerCase().match(/\.([a-z0-9]+)$/)?.[1] ?? '')

// Format of an uploaded file. `.fhr` is ambiguous (FHRMA 6 bytes, OpenCTG 8 bytes): the byte count decides,
// unless the editor explicitly picked the OpenCTG layout.
export function detectTraceFormat(filename: string, byteLength: number, preferred?: TraceFormat | null): TraceFormat | null {
  const ext = extensionOf(filename)
  if (ext === 'rcf') return 'rcf'
  if (ext === 'rcfm' || ext === 'fhrm') return 'rcfm'
  if (ext === 'rcfa') return 'rcfa'
  if (ext !== 'fhr') return null
  if (preferred === 'fhr8' || preferred === 'rcf') return preferred
  const fits = (stride: number) => [4, 8].some((h) => byteLength >= h && (byteLength - h) % stride === 0)
  if (fits(8) && !fits(6)) return 'fhr8'
  return 'rcf'
}

// Header length: 8 (or 12 with a stream code word) after the recorder magic number, else 4. A trailing partial
// sample is ignored (sample counts are floored).
export function detectHeaderBytes(data: Uint8Array, _format?: TraceFormat): 4 | 8 | 12 {
  const view = new DataView(data.buffer, data.byteOffset, data.byteLength)
  if (data.byteLength < 8 || view.getUint32(0, true) !== MAGIC) return 4
  const code = data.byteLength >= 12 ? view.getUint32(8, true) : 0
  return code === CODE_RECORDING || code === CODE_FINISHED ? 12 : 8
}


export type TraceInfo = { format: TraceFormat; headerBytes: 4 | 8 | 12; startEpoch: number; sampleCount: number; durationSeconds: number }

export function parseTraceInfo(data: Uint8Array, format: TraceFormat): TraceInfo {
  const headerBytes = detectHeaderBytes(data, format)
  const view = new DataView(data.buffer, data.byteOffset, data.byteLength)
  const epochAt = headerBytes >= 8 ? 4 : 0
  const startEpoch = data.byteLength >= epochAt + 4 ? view.getUint32(epochAt, true) : 0
  const sampleCount = Math.max(0, Math.floor((data.byteLength - headerBytes) / bytesPerSample(format)))
  return { format, headerBytes, startEpoch, sampleCount, durationSeconds: sampleCount / SAMPLE_RATE_HZ }
}

// Crop window in samples, clamped to the recording (whole seconds; an empty or inverted window = no crop).
export function cropWindow(sampleCount: number, cropStartSeconds?: number | null, cropEndSeconds?: number | null) {
  const clamp = (v: number) => Math.min(Math.max(v, 0), sampleCount)
  const start = clamp(Math.floor(Math.max(0, cropStartSeconds ?? 0)) * SAMPLE_RATE_HZ)
  const end = cropEndSeconds != null && cropEndSeconds > 0 ? clamp(Math.floor(cropEndSeconds) * SAMPLE_RATE_HZ) : sampleCount
  return end > start ? { start, end } : { start: 0, end: sampleCount }
}

// Cropped copy with a normalised 4-byte header (epoch shifted by the crop start). Load it in the viewer with
// layout { headerBytes: 4, bytesPerSample } so nothing is left to auto-detection.
export function cropTrace(data: Uint8Array, format: TraceFormat, cropStartSeconds?: number | null, cropEndSeconds?: number | null) {
  const info = parseTraceInfo(data, format)
  const stride = bytesPerSample(format)
  const { start, end } = cropWindow(info.sampleCount, cropStartSeconds, cropEndSeconds)
  const out = new Uint8Array(4 + (end - start) * stride)
  new DataView(out.buffer).setUint32(0, info.startEpoch + start / SAMPLE_RATE_HZ, true)
  out.set(data.subarray(info.headerBytes + start * stride, info.headerBytes + end * stride), 4)
  return { data: out, startSample: start, sampleCount: end - start, startEpoch: info.startEpoch + start / SAMPLE_RATE_HZ }
}

/* --- Marker text: one marker per line, "<sample index padded to 7> <label>" (fhrviewer format) --- */
export type Marker = [sample: number, label: string]

const MARKER_LINE = /^\s*(\d{1,7})[ \t]+(\S.*?)\s*$/
const MAX_LABEL_LENGTH = 500

export function parseMarkers(text?: string | null): Marker[] {
  const markers: Marker[] = []
  for (const line of (text ?? '').split(/\r?\n/)) {
    const m = MARKER_LINE.exec(line)
    if (m) markers.push([Number(m[1]), m[2].slice(0, MAX_LABEL_LENGTH)])
  }
  return markers.sort((a, b) => a[0] - b[0])
}

// The viewer places new marks at fractional samples and uses `\r` as an in-label line break: both are normalised.
export function serializeMarkers(markers: Marker[]): string {
  return markers
    .map(([s, label]): Marker => [Math.round(s), String(label).replace(/[\r\n]+/g, ' ').trim().slice(0, MAX_LABEL_LENGTH)])
    .filter(([s, label]) => Number.isFinite(s) && s >= 0 && s <= 9_999_999 && label)
    .sort((a, b) => a[0] - b[0])
    .map(([s, label]) => `${String(s).padStart(7, '0')} ${label}`)
    .join('\n')
}

// Server-side validation: 1-based numbers of the lines that are not "<sample> <label>".
export function invalidMarkerLines(text?: string | null): number[] {
  return (text ?? '')
    .split(/\r?\n/)
    .map((line, i) => (line.trim() === '' || MARKER_LINE.test(line) ? 0 : i + 1))
    .filter(Boolean)
}

// Markers of the cropped window, re-indexed from its first sample.
export function shiftMarkers(markers: Marker[], startSample: number, sampleCount: number): Marker[] {
  return markers.map(([s, label]): Marker => [s - startSample, label]).filter(([s]) => s >= 0 && s <= sampleCount)
}

export const formatSeconds = (seconds: number): string => {
  const s = Math.max(0, Math.round(seconds))
  const h = Math.floor(s / 3600)
  const mm = String(Math.floor((s % 3600) / 60)).padStart(2, '0')
  const ss = String(s % 60).padStart(2, '0')
  return h ? `${h}:${mm}:${ss}` : `${mm}:${ss}`
}

// "mm:ss", "h:mm:ss" or plain seconds -> seconds (null if invalid): times typed in the back-office editors.
export function parseTime(text: string): number | null {
  const value = text.trim()
  if (/^\d+(\.\d+)?$/.test(value)) return Number(value)
  const parts = value.split(':')
  if (parts.length < 2 || parts.length > 3 || parts.some((p) => !/^\d+(\.\d+)?$/.test(p))) return null
  return parts.map(Number).reduce((total, part) => total * 60 + part, 0)
}
